import { useContext, useState } from 'react'
import { useNavigate } from 'react-router'
import { AlertCircle, Bell, CheckCircle, Clock, Info, ShieldAlert, X } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { Popover } from './common'
import { POLL_INTERVALS } from '../lib/constants'
import { OrganizationContext } from '../contexts/OrganizationContext'
import styles from './NotificationCenter.module.css'
import { MODERATION_REVIEW_TYPES, notificationBrand, notificationTarget } from './notificationTargets'

function notificationDecisionIcon(notification) {
  return notification?.data?.status === 'rejected'
    ? <X className={styles.iconError} size={16} aria-hidden="true" />
    : <CheckCircle className={styles.iconSuccess} size={16} aria-hidden="true" />
}

function notificationIcon(type, notification) {
  switch (type) {
    case 'post_approval_required':
    case 'post_skip_approval':
      return <AlertCircle className={styles.iconWarning} size={16} aria-hidden="true" />
    case 'post_approved':
    case 'post_published':
      return <CheckCircle className={styles.iconSuccess} size={16} aria-hidden="true" />
    case 'post_rejected':
      return <X className={styles.iconError} size={16} aria-hidden="true" />
    case 'approval_timeout':
      return <Clock className={styles.iconWarning} size={16} aria-hidden="true" />
    case 'moderation_review_required':
      return <ShieldAlert className={styles.iconWarning} size={16} aria-hidden="true" />
    case 'moderation_review_overdue':
      return <Clock className={styles.iconError} size={16} aria-hidden="true" />
    case 'moderation_policy_change':
      return <ShieldAlert className={styles.iconInfo} size={16} aria-hidden="true" />
    case 'moderation_decision':
      return notificationDecisionIcon(notification)
    case 'moderation_rejected':
      return <X className={styles.iconError} size={16} aria-hidden="true" />
    case 'moderation_held':
      return <ShieldAlert className={styles.iconWarning} size={16} aria-hidden="true" />
    case 'moderation_check_failed':
      return <AlertCircle className={styles.iconError} size={16} aria-hidden="true" />
    default:
      return <Info className={styles.iconInfo} size={16} aria-hidden="true" />
  }
}

function formatTime(dateString) {
  const date = new Date(dateString)
  const elapsed = Date.now() - date.getTime()
  const minutes = Math.floor(elapsed / 60000)
  const hours = Math.floor(elapsed / 3600000)
  const days = Math.floor(elapsed / 86400000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}

function notificationCta(notification) {
  return notification.type === 'approval_request'
    || notification.type === 'post_approval_required'
    || MODERATION_REVIEW_TYPES.has(notification.type)
    || notification.data?.post_id
    ? 'See details >'
    : null
}

const NotificationCenter = ({ inline = false }) => {
  const [isOpen, setIsOpen] = useState(inline)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Notices span every brand the viewer reviews. The context is absent
  // outside an OrganizationProvider; the list then names no brand.
  const organizationContext = useContext(OrganizationContext)
  const currentOrgId = organizationContext?.currentOrg?.id
  const organizations = organizationContext?.organizations || []
  const { data, isLoading, error } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => (await api.get('/notifications')).data,
    refetchInterval: POLL_INTERVALS.SLOW,
    refetchIntervalInBackground: false,
    staleTime: POLL_INTERVALS.SLOW,
    refetchOnWindowFocus: true,
  })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['notifications'] })
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId) => api.post(`/notifications/${notificationId}/read`),
    onSuccess: invalidate,
  })
  const markAllAsReadMutation = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: invalidate,
  })
  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId) => api.delete(`/notifications/${notificationId}`),
    onSuccess: invalidate,
  })
  const unreadCount = data?.unread_count || 0
  const notifications = data?.notifications || []

  const handleNotificationClick = async (notification) => {
    // Marking read is not org-scoped and decides nothing below, so it runs
    // beside the switch and navigation; a failure stays on the mutation.
    if (!notification.is_read) markAsReadMutation.mutate(notification.id)
    const target = notificationTarget(notification)
    // A notice about another brand opens in that brand: switch first, and
    // open nothing if the switch is refused (unsaved work, lost access).
    const brand = notificationBrand(notification, currentOrgId, organizations)
    if (target && brand?.slug && organizationContext?.switchOrganization) {
      const switched = await organizationContext.switchOrganization(brand.slug)
      if (!switched) return
    }
    if (target) navigate(target)
    if (!inline) setIsOpen(false)
  }

  const notificationList = (className) => (
    <div className={className}>
      {isLoading ? (
        <div className={styles.loading} role="status">Loading notifications...</div>
      ) : error ? (
        <div className={styles.error} role="alert">Failed to load notifications</div>
      ) : notifications.length === 0 ? (
        <div className={styles.empty}>
          <Bell size={40} className={styles.emptyIcon} aria-hidden="true" />
          <p>No notifications yet</p>
        </div>
      ) : notifications.map((notification) => {
        const brand = notificationBrand(notification, currentOrgId, organizations)
        return (
          <div key={notification.id} className={`${styles.notification} ${!notification.is_read ? styles.unread : ''}`}>
            <button className={styles.notificationAction} type="button" onClick={() => handleNotificationClick(notification)}>
              <span className={styles.notificationIcon}>{notificationIcon(notification.type, notification)}</span>
              <span className={styles.notificationContent}>
                {brand ? <span className={styles.notificationBrand}>{brand.name}</span> : null}
                <span className={styles.notificationTitle}>{notification.title}</span>
                <span className={styles.notificationMessage}>{notification.message}</span>
                <span className={styles.notificationTime}>{formatTime(notification.created_at)}</span>
                {notificationCta(notification) ? <span className={styles.notificationCta}>{notificationCta(notification)}</span> : null}
              </span>
            </button>
            <button
              className={styles.deleteButton}
              type="button"
              onClick={() => deleteNotificationMutation.mutate(notification.id)}
              aria-label={`Delete notification: ${notification.title}`}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        )
      })}
    </div>
  )

  const markAllButton = unreadCount > 0 ? (
    <button
      className={styles.markAllRead}
      type="button"
      onClick={() => markAllAsReadMutation.mutate()}
      disabled={markAllAsReadMutation.isPending}
    >
      Mark all as read
    </button>
  ) : null

  if (inline) {
    return (
      <div className={styles.notificationCenterInline}>
        <div className={styles.notificationContainer}>
          <div className={styles.inlineHeader}>{markAllButton}</div>
          {notificationList(styles.inlineNotificationList)}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.notificationCenter}>
      <Popover
        trigger={(
          <button className={styles.bellButton} type="button" aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}>
            <Bell size={20} aria-hidden="true" />
            {unreadCount > 0 ? <span className={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span> : null}
          </button>
        )}
        open={isOpen}
        onOpenChange={setIsOpen}
        label="Notifications"
        contentClassName={styles.popoverContent}
      >
        <div className={styles.header}><h3>Notifications</h3>{markAllButton}</div>
        {notificationList(styles.notificationList)}
      </Popover>
    </div>
  )
}

export default NotificationCenter
